import React from 'react';
import '../assets/css/SequenceAlignmentPage.css';

const BASE_CELL_WIDTH = 15;

const BASE_COLORS = {
  A: '#9333ea',
  T: '#ef4444',
  G: '#3b82f6',
  C: '#22c55e',
  '-': '#9ca3af',
  N: '#1e293b',
  '|': '#1e293b',
  ' ': 'transparent',
};

const getBaseColor = (base) => BASE_COLORS[base?.toUpperCase()] || '#e2e8f0';

const getPidentClass = (value) => {
  const v = parseFloat(value);
  if (v > 90) return 'pident-hi';
  if (v > 50) return 'pident-md';
  return 'pident-lo';
};

const chunkStr = (str, size) => {
  const chunks = [];
  for (let i = 0; i < str.length; i += size) chunks.push(str.slice(i, i + size));
  return chunks;
};

const calculateConsensus = (qseq, sseq) =>
  qseq.split('').map((q, i) => (q === sseq[i] ? q : 'N')).join('');

function StyledBases({ chunk }) {
  return (
    <>
      {chunk.split('').map((base, i) => (
        <span
          key={i}
          className="sa-base"
          style={{ background: getBaseColor(base) }}
        >
          {base}
        </span>
      ))}
    </>
  );
}

function ConservationRow({ chunk }) {
  return (
    <>
      {chunk.split('').map((base, i) => {
        const conserved = base !== 'N';
        return (
          <span key={i} className="sa-cons-wrap">
            {conserved
              ? <span className="sa-cons-fill" style={{ height: '100%' }} />
              : <span className="sa-cons-empty" />
            }
          </span>
        );
      })}
    </>
  );
}

function SequenceLogoSvg({ sequenceChunk }) {
  const w = sequenceChunk.length * BASE_CELL_WIDTH;
  return (
    <svg width={w} height={36} xmlns="http://www.w3.org/2000/svg">
      <text fontFamily="'Courier New', monospace" fontSize={11} textAnchor="middle">
        {sequenceChunk.map((base, i) => {
          const x = i * BASE_CELL_WIDTH + BASE_CELL_WIDTH / 2;
          if (Array.isArray(base)) {
            return (
              <React.Fragment key={i}>
                <tspan fill={getBaseColor(base[0])} x={x} y={14}>{base[0]}</tspan>
                <tspan fill={getBaseColor(base[1])} x={x} y={28}>{base[1]}</tspan>
              </React.Fragment>
            );
          }
          return <tspan key={i} fill={getBaseColor(base)} x={x} y={22}>{base}</tspan>;
        })}
      </text>
    </svg>
  );
}

function generateSequenceLogo(qseq, sseq) {
  const logos = [];
  for (let i = 0; i < qseq.length; i += 50) {
    const chunk = [];
    for (let j = i; j < i + 50 && j < qseq.length; j++) {
      const q = qseq[j], s = sseq[j];
      if (q === s) chunk.push(q.toUpperCase());
      else if (s === '-') chunk.push(q.toLowerCase());
      else chunk.push([q.toUpperCase(), s.toUpperCase()]);
    }
    logos.push(chunk);
  }
  return logos;
}

const META_ROWS = [
  ['Run', 'pident'],
  ['qlen', 'len'],
  ['mm', 'gap'],
  ['qstart', 'sstart'],
  ['send', 'evalue'],
  ['bit', 'poltype'],
  ['polstart', 'polend'],
];

function SequenceAlignment({ alignmentData }) {
  if (!alignmentData) return null;

  const { qseq = '', sseq = '' } = alignmentData;

  const qseqChunks = chunkStr(qseq, 50);
  const sseqChunks = chunkStr(sseq, 50);
  const consensusChunks = qseqChunks.map((q, i) => calculateConsensus(q, sseqChunks[i] || ''));
  const logoChunks = generateSequenceLogo(qseq, sseq);

  return (
    <div className="sa-wrap">
      {/* ── Color legend ── */}
      <div className="sa-legend">
        {[
          { color: '#fef2f2', label: 'pident > 90 (High)' },
          { color: '#fefce8', label: 'pident > 50 (Medium)' },
          { color: '#f0fdf4', label: 'pident ≤ 50 (Low)' },
          { color: '#f0f0f0', label: 'Default' },
        ].map(({ color, label }) => (
          <div key={label} className="sa-legend-item">
            <span className="sa-legend-swatch" style={{ background: color, border: '1px solid #e2e8f0' }} />
            {label}
          </div>
        ))}
      </div>

      {/* ── Metadata table ── */}
      <div className="sa-meta">
        <table>
          <tbody>
            {META_ROWS.map(([left, right]) => (
              <tr key={left}>
                <td className="sa-meta-key">{left}:</td>
                <td className={`sa-meta-val ${left === 'pident' || right === 'pident' ? getPidentClass(alignmentData.pident) : ''}`}>
                  {alignmentData[left] ?? '—'}
                </td>
                <td className="sa-meta-key">{right}:</td>
                <td className={`sa-meta-val ${right === 'pident' ? getPidentClass(alignmentData.pident) : ''}`}>
                  {alignmentData[right] ?? '—'}
                </td>
              </tr>
            ))}
            {/* Sequences (full row, truncated) */}
            <tr>
              <td className="sa-meta-key">qseq:</td>
              <td className="sa-meta-val" colSpan={3}>
                <span className="sa-meta-seq-val" title={qseq}>{qseq || '—'}</span>
              </td>
            </tr>
            <tr>
              <td className="sa-meta-key">sseq:</td>
              <td className="sa-meta-val" colSpan={3}>
                <span className="sa-meta-seq-val" title={sseq}>{sseq || '—'}</span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* ── Sequence alignment blocks ── */}
      {qseqChunks.map((qChunk, idx) => {
        const sChunk = sseqChunks[idx] || '';
        const cChunk = consensusChunks[idx] || '';
        const subChunks = qChunk.match(/.{1,10}/g) || [];
        const sSubChunks = sChunk.match(/.{1,10}/g) || [];
        const cSubChunks = cChunk.match(/.{1,10}/g) || [];
        const logoSubChunks = logoChunks[idx]
          ? chunkStr(logoChunks[idx], 10)
          : [];
        const start = idx * 50 + 1;
        const end = Math.min((idx + 1) * 50, qseq.length);

        return (
          <div key={idx} className="sa-block">
            <div className="sa-block-title">Sequence Alignment {start} – {end}</div>
            <div className="sa-block-inner">
              <table className="sa-seq-table">
                <tbody>
                  {/* QSEQ */}
                  <tr>
                    <td className="sa-seq-lbl">Complete QSEQ</td>
                    {subChunks.map((chunk, ci) => (
                      <td key={ci} className="sa-seq-td">
                        <StyledBases chunk={chunk} />
                      </td>
                    ))}
                  </tr>
                  {/* SSEQ */}
                  <tr>
                    <td className="sa-seq-lbl">Complete SSEQ</td>
                    {sSubChunks.map((chunk, ci) => (
                      <td key={ci} className="sa-seq-td">
                        <StyledBases chunk={chunk} />
                      </td>
                    ))}
                  </tr>
                  {/* Consensus */}
                  <tr>
                    <td className="sa-seq-lbl">Consensus</td>
                    {cSubChunks.map((chunk, ci) => (
                      <td key={ci} className="sa-seq-td">
                        <StyledBases chunk={chunk} />
                      </td>
                    ))}
                  </tr>
                  {/* Conservation */}
                  <tr>
                    <td className="sa-seq-lbl">Conservation</td>
                    {cSubChunks.map((chunk, ci) => (
                      <td key={ci} className="sa-seq-td" style={{ height: 22 }}>
                        <ConservationRow chunk={chunk} />
                      </td>
                    ))}
                  </tr>
                  {/* Sequence Logo */}
                  <tr>
                    <td className="sa-seq-lbl">Sequence Logo</td>
                    {logoSubChunks.map((chunk, ci) => (
                      <td key={ci} className="sa-logo-td" style={{ padding: 0 }}>
                        <SequenceLogoSvg sequenceChunk={chunk} />
                      </td>
                    ))}
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        );
      })}

      {/* ── Info panel ── */}
      <div className="sa-info">
        <div className="sa-info-title">Sequence Alignment Information</div>
        <div className="sa-info-body">
          Sequence alignment made between the SRA accession (QSEQ) and poliovirus genomes 1, 2, and 3 (SSEQ).
          The consensus sequence between the QSEQ and SSEQ is also shown.
          Conservation bar graphs indicate whether each position was conserved or contained a gap/mismatch.
          The sequence logo shows conserved bases as large letters, mismatches as two stacked small letters,
          and partial identifications as a single small letter.
        </div>
        <div className="sa-info-color-grid" style={{ marginTop: 10 }}>
          {[
            { color: '#ef4444', label: 'Thymine (T)' },
            { color: '#9333ea', label: 'Adenine (A)' },
            { color: '#22c55e', label: 'Cytosine (C)' },
            { color: '#3b82f6', label: 'Guanine (G)' },
            { color: '#1e293b', label: 'Unknown (N)' },
            { color: '#9ca3af', label: 'Gap (-)' },
          ].map(({ color, label }) => (
            <div key={label} className="sa-info-color-item">
              <span className="sa-info-swatch" style={{ background: color }} />
              {label}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default SequenceAlignment;
